import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { askAI } from '../services/api';

function AskAI({ onLoadSQL, editingWidget, widgetSqlRef }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    if (trimmed === '/clear') {
      setMessages([]);
      setInput('');
      return;
    }

    const userMsg = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      // If editing a widget, inject its context into the prompt
      let prompt = trimmed;
      if (editingWidget && widgetSqlRef?.current) {
        prompt = `I'm editing a widget titled "${editingWidget.title}" (chart type: ${editingWidget.chartType}). Its current SQL is:\n\`\`\`sql\n${widgetSqlRef.current}\n\`\`\`\nMy request: ${trimmed}`;
      }
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const data = await askAI(prompt, history);
      setMessages([...newMessages, {
        role: 'assistant',
        content: data.reply,
        sql: data.sql || null,
      }]);
    } catch (err) {
      setMessages([...newMessages, {
        role: 'assistant',
        content: `Error: ${err.message}`,
        sql: null,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessage = (content) => {
    // Strip SQL code blocks since they're shown separately via the sql field
    const cleaned = content.replace(/```sql[\s\S]*?```/g, '').trim();
    return <Markdown>{cleaned}</Markdown>;
  };

  if (!isOpen) {
    return (
      <button className="ask-ai-toggle" onClick={() => setIsOpen(true)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Ask AI
      </button>
    );
  }

  return (
    <div className="ask-ai-window">
      <div className="ask-ai-header">
        <span>Ask AI</span>
        <button className="ask-ai-close" onClick={() => setIsOpen(false)}>
          &times;
        </button>
      </div>

      <div className="ask-ai-messages">
        {messages.length === 0 && (
          <div className="ask-ai-placeholder">
            Ask any question about any metric and I'll generate a query for you.
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`ask-ai-msg ask-ai-msg-${msg.role}`}>
            <div className="ask-ai-msg-content">
              {msg.role === 'user' ? msg.content : formatMessage(msg.content)}
            </div>
            {msg.sql && (
              <div className="ask-ai-sql-block">
                <pre>{msg.sql}</pre>
                <button
                  className="ask-ai-load-btn"
                  onClick={() => onLoadSQL(msg.sql)}
                >
                  {editingWidget ? 'Load into Widget' : 'Load into Editor'}
                </button>
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="ask-ai-msg ask-ai-msg-assistant">
            <div className="ask-ai-msg-content ask-ai-typing">
              Thinking
              <span className="bouncing-dots">
                <span className="dot">.</span>
                <span className="dot">.</span>
                <span className="dot">.</span>
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="ask-ai-input-area">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. Show me weekly PR count..."
          disabled={isLoading}
        />
        <button onClick={handleSend} disabled={isLoading || !input.trim()}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default AskAI;
